import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import PrecisionManufacturingRoundedIcon from "@mui/icons-material/PrecisionManufacturingRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import PageFrame from "./PageFrame";
import api from "../bootstrapApi";
import { saasHeroActionButtonSx, saasSectionCardSx } from "../components/saasStyles";

const kpiCardSx = {
  borderRadius: "30px",
  border: "1px solid #dfe6f4",
  background: "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(247,249,252,0.98) 100%)",
  boxShadow: "0 18px 38px rgba(31,45,72,0.07)",
  p: { xs: 2.2, md: 2.6 },
  minHeight: 180,
  display: "grid",
  gap: 1.25,
};

const glowCardSx = {
  ...kpiCardSx,
  background:
    "radial-gradient(circle at top right, rgba(95,111,255,0.16), transparent 28%), linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(247,249,252,0.98) 100%)",
};

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let active = true;

    Promise.all([
      api.get("/api/bootstrap"),
      api.get("/api/leads"),
      api.get("/api/orders"),
      api.get("/api/technicians"),
      api.get("/api/pmo/queue").catch(() => ({ data: [] })),
      api.get("/api/products").catch(() => ({ data: [] })),
    ])
      .then(([bootstrapResponse, leadsResponse, ordersResponse, techniciansResponse, pmoResponse, productsResponse]) => {
        if (!active) {
          return;
        }

        const leads = leadsResponse.data;
        const orders = ordersResponse.data;
        const technicians = techniciansResponse.data;
        const pmoQueue = pmoResponse.data;
        const products = productsResponse.data;

        setData({
          bootstrap: bootstrapResponse.data,
          leads,
          orders,
          technicians,
          pmoQueue,
          products,
        });
      })
      .catch(() => {
        if (active) {
          setMessage({ type: "error", text: "Les donnees du tableau de bord n'ont pas pu etre chargees." });
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const metrics = useMemo(() => {
    if (!data) {
      return null;
    }

    const { leads, orders, technicians, pmoQueue, products } = data;
    const newLeads = leads.filter((lead) => lead.status === "lead_new").length;
    const qualifiedLeads = leads.filter((lead) => lead.status === "lead_qualified").length;
    const wonLeads = leads.filter((lead) => lead.status === "lead_won").length;
    const openOrders = orders.filter((order) => order.status === "order_open").length;
    const readyOrders = orders.filter((order) => order.status === "ready_for_delivery").length;
    const appointmentOrders = orders.filter((order) => order.status === "appointment_proposed" || order.status === "appointment_confirmed").length;
    const activeTechnicians = technicians.filter((technician) => technician.active).length;
    const whatsappReady = technicians.filter((technician) => technician.whatsapp_number).length;
    const standardProducts = products.filter((product) => product.product_type === "standard").length;
    const customProducts = products.filter((product) => product.product_type === "custom").length;

    return {
      totalLeads: leads.length,
      newLeads,
      qualifiedLeads,
      wonLeads,
      openOrders,
      readyOrders,
      appointmentOrders,
      activeTechnicians,
      whatsappReady,
      totalProducts: products.length,
      standardProducts,
      customProducts,
      pmoQueue: pmoQueue.length,
      leadConversion: leads.length ? Math.round((wonLeads / leads.length) * 100) : 0,
      deliveryReadiness: orders.length ? Math.round((readyOrders / orders.length) * 100) : 0,
      technicianCoverage: technicians.length ? Math.round((whatsappReady / technicians.length) * 100) : 0,
    };
  }, [data]);

  const recentLeads = data?.leads?.slice(0, 4) || [];
  const recentOrders = data?.orders?.slice(0, 4) || [];
  const pipelineChips = data?.bootstrap?.statuses?.orders || [];

  return (
    <PageFrame
      eyebrow="SmartDoctors"
      title="Tableau de bord"
      description="Pilotage central des prospects, des commandes, du PMO et de la coordination terrain."
      stats={[]}
      action={
        <Button variant="contained" startIcon={<AddCircleRoundedIcon />} sx={saasHeroActionButtonSx}>
          + Nouvelle action
        </Button>
      }
    >
      {message ? <Alert severity={message.type}>{message.text}</Alert> : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1.5fr 0.9fr" },
          gap: 3,
        }}
      >
        <Box sx={glowCardSx}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box>
              <Typography variant="overline" sx={{ color: "#6f7c96", letterSpacing: "0.12em" }}>
                KPIs EN DIRECT
              </Typography>
              <Typography variant="h4" sx={{ color: "#25324b", mb: 0.75 }}>
                {metrics?.totalLeads || 0} prospects dans le systeme
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
                Focus sur la conversion, la preparation livraison et l'execution terrain.
              </Typography>
            </Box>
            <Box
              sx={{
                width: 58,
                height: 58,
                borderRadius: "20px",
                bgcolor: "rgba(95,111,255,0.12)",
                color: "#5d6dff",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <SupportAgentRoundedIcon />
            </Box>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 1.5,
              mt: 1,
            }}
          >
            <MiniMetric title="Nouveaux prospects" value={metrics?.newLeads || 0} helper={`${metrics?.qualifiedLeads || 0} qualifies`} tone="#5b67f6" />
            <MiniMetric title="Commandes ouvertes" value={metrics?.openOrders || 0} helper={`${metrics?.readyOrders || 0} pretes`} tone="#2d9cdb" />
            <MiniMetric title="File PMO" value={metrics?.pmoQueue || 0} helper={`${metrics?.appointmentOrders || 0} rendez-vous`} tone="#20b486" />
          </Box>
        </Box>

        <Box sx={kpiCardSx}>
          <Typography variant="overline" sx={{ color: "#6f7c96", letterSpacing: "0.12em" }}>
            SANTE DU PIPELINE
          </Typography>
          <KpiProgress label="Conversion prospects" value={metrics?.leadConversion || 0} color="#5b67f6" />
          <KpiProgress label="Preparation livraison" value={metrics?.deliveryReadiness || 0} color="#20b486" />
          <KpiProgress label="Couverture techniciens" value={metrics?.technicianCoverage || 0} color="#2d9cdb" />
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", xl: "repeat(4, 1fr)" },
          gap: 2.2,
        }}
      >
        <KpiPanel
          icon={<SupportAgentRoundedIcon />}
          title="Moteur prospects"
          value={metrics?.totalLeads || 0}
          subtitle={`${metrics?.newLeads || 0} neu / ${metrics?.wonLeads || 0} gewonnen`}
          tone="rgba(95,111,255,0.12)"
          color="#5b67f6"
        />
        <KpiPanel
          icon={<LocalShippingRoundedIcon />}
          title="Flux commandes"
          value={data?.orders?.length || 0}
          subtitle={`${metrics?.openOrders || 0} ouvertes / ${metrics?.readyOrders || 0} pretes a livrer`}
          tone="rgba(32,180,134,0.12)"
          color="#20b486"
        />
        <KpiPanel
          icon={<PrecisionManufacturingRoundedIcon />}
          title="Equipe technique"
          value={metrics?.activeTechnicians || 0}
          subtitle={`${metrics?.whatsappReady || 0} joignables via WhatsApp`}
          tone="rgba(45,156,219,0.12)"
          color="#2d9cdb"
        />
        <KpiPanel
          icon={<Inventory2RoundedIcon />}
          title="Catalogue produits"
          value={metrics?.totalProducts || 0}
          subtitle={`${metrics?.standardProducts || 0} standards / ${metrics?.customProducts || 0} sur mesure`}
          tone="rgba(246,166,35,0.12)"
          color="#f6a623"
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: "1.1fr 0.9fr" },
          gap: 3,
        }}
      >
        <Box sx={{ ...saasSectionCardSx, borderRadius: "34px", display: "grid", gap: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="h6" sx={{ color: "#25324b", mb: 0.4 }}>
                Flux prospects
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Derniers contacts et avancement commercial en cours.
              </Typography>
            </Box>
            <ForumRoundedIcon sx={{ color: "#5b67f6" }} />
          </Stack>

          <Stack spacing={1.35}>
            {recentLeads.map((lead) => (
              <ActivityRow
                key={lead.id}
                title={lead.contactName || "Prospect inconnu"}
                subtitle={lead.items.map((item) => item.productName).join(", ") || "Aucun produit"}
                meta={lead.status}
                accent={lead.status === "lead_new" ? "#5b67f6" : "#20b486"}
              />
            ))}
          </Stack>
        </Box>

        <Box sx={{ ...saasSectionCardSx, borderRadius: "34px", display: "grid", gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ color: "#25324b", mb: 0.4 }}>
              Radar commandes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Vue rapide des commandes en cours et des points de friction operationnels.
            </Typography>
          </Box>

          <Stack spacing={1.35}>
            {recentOrders.map((order) => (
              <ActivityRow
                key={order.id}
                title={order.contactName || `Commande ${order.id}`}
                subtitle={order.items.map((item) => item.productName).join(", ") || "Aucun produit"}
                meta={order.status}
                accent={order.status === "ready_for_delivery" ? "#20b486" : "#2d9cdb"}
              />
            ))}
          </Stack>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: "0.95fr 1.05fr" },
          gap: 3,
        }}
      >
        <Box sx={{ ...saasSectionCardSx, borderRadius: "34px", display: "grid", gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ color: "#25324b", mb: 0.4 }}>
              Apercu des modules
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Perimetre actif du systeme et modele de statuts de la plateforme.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {(data?.bootstrap?.modules || []).map((module) => (
              <Chip
                key={module}
                label={module}
                sx={{
                  borderRadius: "999px",
                  bgcolor: "rgba(95,111,255,0.08)",
                  color: "#5b67f6",
                  fontWeight: 700,
                }}
              />
            ))}
          </Stack>

          <Typography variant="subtitle2" sx={{ color: "#25324b", mt: 0.5 }}>
            Carte des statuts
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {pipelineChips.map((status) => (
              <Chip
                key={status}
                label={status}
                size="small"
                sx={{
                  borderRadius: "999px",
                  bgcolor: "rgba(37,50,75,0.06)",
                  color: "#42506a",
                }}
              />
            ))}
          </Stack>
        </Box>

        <Box sx={{ ...saasSectionCardSx, borderRadius: "34px", display: "grid", gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ color: "#25324b", mb: 0.4 }}>
              Focus du jour
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Priorites operationnelles a partir des donnees actuelles.
            </Typography>
          </Box>

          <FocusCard
            title="Commercial"
            text={`${metrics?.newLeads || 0} nouveaux prospects attendent un premier contact. ${metrics?.leadConversion || 0}% de conversion sur l'ensemble des prospects.`}
            color="#5b67f6"
          />
          <FocusCard
            title="PMO"
            text={`${metrics?.pmoQueue || 0} dossiers dans la file. ${metrics?.appointmentOrders || 0} commandes sont deja en phase de rendez-vous.`}
            color="#20b486"
          />
          <FocusCard
            title="Technique"
            text={`${metrics?.activeTechnicians || 0} techniciens actifs, dont ${metrics?.whatsappReady || 0} joignables numeriquement.`}
            color="#2d9cdb"
          />
        </Box>
      </Box>
    </PageFrame>
  );
}

function MiniMetric({ title, value, helper, tone }) {
  return (
    <Box
      sx={{
        p: 1.6,
        borderRadius: "22px",
        border: "1px solid #dfe6f4",
        bgcolor: "#fff",
        boxShadow: "0 10px 24px rgba(31,45,72,0.05)",
      }}
    >
      <Typography variant="caption" sx={{ color: "#7c89a1" }}>
        {title}
      </Typography>
      <Typography variant="h4" sx={{ color: tone, lineHeight: 1.1, mt: 0.55 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {helper}
      </Typography>
    </Box>
  );
}

function KpiPanel({ icon, title, value, subtitle, tone, color }) {
  return (
    <Box sx={kpiCardSx}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="caption" sx={{ color: "#7c89a1" }}>
            {title}
          </Typography>
          <Typography variant="h3" sx={{ color: "#25324b", mt: 0.55 }}>
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: "18px",
            bgcolor: tone,
            color,
            display: "grid",
            placeItems: "center",
          }}
        >
          {icon}
        </Box>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>
    </Box>
  );
}

function KpiProgress({ label, value, color }) {
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.8 }}>
        <Typography variant="body2" sx={{ color: "#42506a", fontWeight: 700 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color, fontWeight: 800 }}>
          {value}%
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.min(value, 100)}
        sx={{
          height: 10,
          borderRadius: 999,
          bgcolor: "rgba(66,80,106,0.08)",
          "& .MuiLinearProgress-bar": {
            borderRadius: 999,
            background: color,
          },
        }}
      />
    </Box>
  );
}

function ActivityRow({ title, subtitle, meta, accent }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "10px 1fr auto",
        gap: 1.3,
        alignItems: "center",
        borderRadius: "22px",
        border: "1px solid #e1e7f2",
        bgcolor: "#fff",
        p: 1.4,
      }}
    >
      <Box
        sx={{
          width: 10,
          height: 42,
          borderRadius: 999,
          bgcolor: accent,
        }}
      />
      <Box>
        <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 800 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
      <Chip
        label={meta}
        size="small"
        sx={{
          borderRadius: "999px",
          bgcolor: "rgba(95,111,255,0.08)",
          color: "#5b67f6",
          fontWeight: 700,
        }}
      />
    </Box>
  );
}

function FocusCard({ title, text, color }) {
  return (
    <Box
      sx={{
        borderRadius: "22px",
        border: "1px solid #e1e7f2",
        bgcolor: "#fff",
        p: 1.6,
        boxShadow: "0 10px 24px rgba(31,45,72,0.04)",
      }}
    >
      <Typography variant="subtitle2" sx={{ color, fontWeight: 800, mb: 0.55 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {text}
      </Typography>
    </Box>
  );
}
